import { type SyntheticEvent, useCallback } from "react";
import {
  type FieldValues,
  type Path,
  useController,
  type UseControllerProps,
  useFormContext,
} from "react-hook-form";
import { transformValidationOptions } from "@/components/common/formElements/utils/transformValidationOptions";
import { useTranslation } from "@/i18n/client";

export interface UseCheckboxFieldControllerProps<
  TFieldValues extends FieldValues,
> extends Omit<UseControllerProps<TFieldValues>, "control"> {
  groupName?: Path<TFieldValues>;
  value?: string | number | boolean;
  multiple?: boolean;
  onChange?: (event: SyntheticEvent, checked: boolean) => void;
}

export function useCheckboxFieldController<TFieldValues extends FieldValues>(
  props: UseCheckboxFieldControllerProps<TFieldValues>,
) {
  const {
    value,
    multiple,
    onChange,
    rules,
    groupName,
    ...restControllerProps
  } = props;

  const { t } = useTranslation("form-elements");
  const { control, trigger } = useFormContext<TFieldValues>();

  const checkboxFieldRules = transformValidationOptions<TFieldValues>(t, rules);
  const required = typeof checkboxFieldRules.required !== "undefined";

  const controllerProps = useController<TFieldValues>({
    control,
    rules: checkboxFieldRules,
    ...restControllerProps,
  });
  const { field } = controllerProps;

  const handleMultipleChange = useCallback(
    (event: { target: EventTarget }, checked: boolean) => {
      const multipleValues = (field.value as string[]) || [];
      const targetValue = (event.target as HTMLInputElement).value;

      const updatedValues = checked
        ? [...multipleValues, targetValue]
        : multipleValues.filter(
            (item: UseCheckboxFieldControllerProps<TFieldValues>["value"]) =>
              item && item.toString() !== targetValue.toString(),
          );

      field.onChange(updatedValues);
    },
    [field],
  );

  const handleChange = useCallback(
    (event: SyntheticEvent, checked: boolean) => {
      if (multiple) {
        handleMultipleChange(event, checked);
      } else {
        field.onChange(event, checked);
      }

      if (onChange) {
        onChange(event, checked);
      }

      if (groupName) {
        trigger(groupName);
      }
    },
    [field, multiple, handleMultipleChange, onChange, trigger, groupName],
  );

  const isChecked = multiple
    ? ((field.value as string[]) || []).includes(value?.toString() as string)
    : !!field.value;

  return {
    required,
    isChecked,
    onChangeField: handleChange,
    ...controllerProps,
  };
}
